import React from 'react';

import { useAppSelector } from '../../store';

import PageLoader from '@/components/page-loader';

import Breadcrumbs from '@/components/modules/breadcrumbs';
import LanguageSwitcher from '@/components/modules/language-switcher';
import CurrencySelector from '@/components/modules/currency-selector';
import DataSourceTools from '@/components/modules/data-source-tools';
import DataSplitter from '@/components/modules/data-splitter';
import Observer from '@/components/modules/observer';
import PathChange from '@/components/modules/path-change';
import Cookies from '@/components/modules/cookies';
import SimpleSwitch from '@/components/modules/simple-switch';
import SimpleTabsSwitch from '@/components/modules/simple-tabs-switch';
import SliderRange from '@/components/modules/slider-range';
import Pagination from '@/components/modules/pagination';
import PaginationCircular from '@/components/modules/pagination-circular';
import ListIteration from '@/components/modules/list-iteration';
import Resize from '@/components/modules/resize';

// Spark
import LoginModule from '@/components/modules/login';
import RegisterModule from '@/components/modules/register';
import SparkConfigurator from '@/components/modules/spark/configurator';
import StripePayment from '@/components/modules/spark/stripe-payment';
import Highlights from '@/components/modules/spark/highlights';
import MarketScan from '@/components/modules/spark/market-scan';
import Deploy from '@/components/modules/deploy';
import Countries from '@/components/modules/countries';

import PageHome from '@/components/modules/pages/home';
import PageProduct from '@/components/modules/pages/product';
// NOTE: the nova pages are no longer mounted here — they are served by
// fixed React routes (src/nova/router.tsx) wired in
// src/routes.tsx, not by moduleKey/ModuleElement.
import PageStats from '@/components/modules/pages/stats';
import ChartJS from '@/components/modules/chart-js';
import HlsVideo from '@/components/modules/hls-video';
import HtmlContent from '@/components/modules/html-content';
import NumberFlow from '@/components/modules/number-flow';
import { BrandSelector as BrandsSelector } from '@/nova/components/BrandSelector/BrandSelector';
import StudioNavigation from '@/components/modules/studio-navigation';
// Mounted via fixed React routes (`/account/wallet`, `/account/wallet/budget`) instead — see src/nova/router.tsx.
// import Wallet from '@/components/modules/pages/wallet';
// import AdBudgetLimit from '@/components/modules/pages/wallet/budget';

export const ModuleElement = (componentProps) => {
  const config = useAppSelector((state) => state.templatesConfig);
  const dsType = componentProps.properties?.dsType ?? '';
  const det = dsType ? config.dataElementTypes[dsType] : null;

  let moduleKey = '';
  if (det && det.internal && det.internal.length) {
    const from = det.internal.find((el) => el.id === 'from');
    const path = det.internal.find((el) => el.id === 'path');
    if (from && from.value === 'module') {
      if (path && path.value) moduleKey = path.value;
    }
  }

  if (!moduleKey) return null;

  let content = null;

  switch (moduleKey) {
    case 'module.languageSwitcher':
      content = <LanguageSwitcher {...componentProps} />;
      break;

    case 'module.currencySelector':
      content = <CurrencySelector {...componentProps} />;
      break;

    case 'module.dataSourceTools':
      content = <DataSourceTools {...componentProps} />;
      break;

    case 'module.cookies':
      content = null;
      if (window?.config?.disableCookies !== '1') {
        content = <Cookies {...componentProps} />;
      }
      break;

    case 'module.breadcrumbs':
      content = <Breadcrumbs {...componentProps} />;
      break;

    case 'module.data-splitter':
      content = <DataSplitter {...componentProps} />;
      break;

    case 'module.observer':
      content = <Observer {...componentProps} />;
      break;

    case 'module.path-change':
      content = <PathChange {...componentProps} />;
      break;

    case 'module.simple-switch':
      content = <SimpleSwitch {...componentProps} />;
      break;

    case 'module.simple-tabs-switch':
      content = <SimpleTabsSwitch {...componentProps} />;
      break;

    case 'module.slider-range':
      content = <SliderRange {...componentProps} />;
      break;

    case 'module.pagination':
      content = <Pagination {...componentProps} />;
      break;

    case 'module.pagination-circular':
      content = <PaginationCircular {...componentProps} />;
      break;

    case 'module.list.iteration':
      content = <ListIteration {...componentProps} />;
      break;

    case 'module.resize':
      content = <Resize {...componentProps} />;
      break;

    case 'module.owners.hub.login':
      content = <LoginModule {...componentProps} />;
      break;

    case 'module.owners.hub.register':
      content = <RegisterModule {...componentProps} isRegister={true} />;
      break;

    case 'module.spark.configurator':
      content = <SparkConfigurator {...componentProps} />;
      break;

    case 'module.spark.configurator.payment':
      content = <StripePayment {...componentProps} />;
      break;

    case 'module.spark.configurator.loader':
      content = <PageLoader {...componentProps} />;
      break;

    case 'module.spark.highlights.carousel':
      content = <Highlights {...componentProps} />;
      break;

    case 'oh.module.market-scan':
      content = <MarketScan {...componentProps} />;
      break;

    case 'module.spark.deploy':
      content = <Deploy {...componentProps} />;
      break;

    case 'module.spark.countries':
      content = <Countries {...componentProps} />;
      break;

    case 'oh.module.home':
      content = <PageHome {...componentProps} />;
      break;

    case 'oh.module.product':
      content = <PageProduct {...componentProps} />;
      break;

    case 'oh.module.stats':
      content = <PageStats {...componentProps} />;
      break;

    case 'oh.module.chart-js':
      content = <ChartJS {...componentProps} />;
      break;

    case 'oh.module.hls-video':
      content = <HlsVideo {...componentProps} />;
      break;

    case 'oh.module.html-content':
      content = <HtmlContent {...componentProps} />;
      break;

    case 'oh.module.number-flow':
      content = <NumberFlow {...componentProps} />;
      break;

    // Mounted via fixed React routes (`/account/wallet`, `/account/wallet/budget`) instead — see src/nova/router.tsx.
    // case 'oh.module.wallet':
    //   content = <Wallet {...componentProps} />;
    //   break;

    // case 'oh.module.ad-budget-limit':
    //   content = <AdBudgetLimit {...componentProps} />;
    //   break;

    case 'oh.module.brands-selector':
      content = <BrandsSelector {...componentProps} />;
      break;

    case 'oh.module.studio-navigation':
      content = <StudioNavigation {...componentProps} />;
      break;

    default:
      break;
  }

  if (!content) return null;

  return content;
};
